import { BigNumber } from "@ethersproject/bignumber";
import { formatUnits } from "@ethersproject/units";
import { flow, identity, pipe } from "fp-ts/lib/function";
import { match, P } from "ts-pattern";
import {
  BigIntWithDecimals,
  BigNumberWithDecimals,
  ChecksumAddress,
  DEFAULT_DECIMALS,
  PRECISION_DECIMALS,
} from "@silo/types";
import { parseBigIntWithDecimals } from "./bigint-with-decimals";
import { shiftDecimal } from "./bigint";

export const shortenValue = (
  value: string | number,
  firstN: number,
  lastN: number
) =>
  pipe(
    value,
    String,
    (str) => `${str.slice(0, firstN)}...${str.slice(-lastN)}`
  );

export const shortenAddress = (address: ChecksumAddress) =>
  shortenHash(address);

export const shortenHash = (address: string) => shortenValue(address, 6, 4);

const SIGNIFICANT_DIGITS = 6;
const SIGNIFICANT_DIGIT_THRESHOLD = 10 ** SIGNIFICANT_DIGITS;

/**
 * Removes non-numeric characters from a string (i.e. `$`, `,`)
 */
export const stripNonNumeric = (str: string): string =>
  str.replace(/[^-\d.]/g, "");

/**
 * Strips non-numeric characters before running `parseFloat`
 *
 * ```ts
 * parseFloat("$4.25") // => NaN
 * parseNumber("$4.25") // => 4.25
 * ```
 */
export const parseNumber = flow(stripNonNumeric, parseFloat);

type ValueToFormat = Parameters<typeof normalizeValue>[0];

export const normalizeValue = flow(
  (
    value:
      | number
      | string
      | BigNumber
      | BigNumberWithDecimals
      | bigint
      | BigIntWithDecimals
  ): number =>
    match(value)
      .with(P.number, identity)
      .with(P.string, parseNumber)
      // TODO: look into why flow is causing a unit test error
      .with(P.when(BigNumber.isBigNumber), (value) =>
        pipe(value, formatUnits, parseNumber)
      )
      .with({ bigNumber: P._ }, ({ bigNumber, decimals }) => {
        const str = formatUnits(bigNumber, decimals);

        return parseNumber(str);
      })
      .with(P.bigint, (value) => pipe(value, formatUnits, parseNumber))
      .with({ value: P._ }, ({ value, decimals }) => {
        const str = formatUnits(value, decimals);

        return parseNumber(str);
      })
      .exhaustive(),
  (value) => (value === -0 ? 0 : value)
);

/**
 * Two formatters are used since `maximumSignificantDigits` and
 * `maximumFractionDigits` cannot currently be used together. The new
 * `roundingPriority` prop will ultimately fix this:
 * https://caniuse.com/mdn-javascript_builtins_intl_numberformat_numberformat_options_roundingpriority_parameter
 */
export const formatAsDecimal = (
  value: number,
  maxDecimals?: number
): string => {
  if (value >= SIGNIFICANT_DIGIT_THRESHOLD) {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return formatter.format(value);
  }

  const maxDigitsFormatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
    maximumSignificantDigits: SIGNIFICANT_DIGITS,
  });

  const result = maxDigitsFormatter.format(value);

  if (!maxDecimals) return result;

  const maxDecimalsFormatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
    maximumFractionDigits: maxDecimals,
  });

  const resultAsNumber = parseNumber(result);

  return maxDecimalsFormatter.format(resultAsNumber);
};

/**
 * Formats a `number`, `string`, or `BigNumber` as a decimal. Typically used for token amounts.
 *
 * Uses 6 max significant digits unless the value is greater than or equal to 1
 * million, in which case the value is displayed as an integer.
 *
 * ```
 * asDecimal(10) => '10'
 * asDecimal(1.23456789) => '1.23457'
 * asDecimal('388923.12') => '388,923'
 * asDecimal(parseUnits('21388923.12')) => '21,388,923'
 * ```
 */
export const asDecimal = (value: Parameters<typeof normalizeValue>[0]) =>
  match(value)
    .with({ decimals: P.number }, (value) =>
      pipe(value, normalizeValue, (normalized) =>
        formatAsDecimal(normalized, value.decimals - 2)
      )
    )
    .otherwise(flow(normalizeValue, formatAsDecimal));

const compactDecimalFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
  notation: "compact",
});

export const formatAsCompactDecimal = (value: number): string => {
  if (value < 1000) return formatAsDecimal(value, 3);

  return compactDecimalFormatter.format(value);
};

/**
 * Formats a `number`, `string`, or `BigNumber` as a compact decimal amount.
 *
 * ```
 * asCompactDecimal(500) => '500'
 * asCompactDecimal(1500) => '1.5K'
 * asCompactDecimal('900750') => '900.8K'
 * asCompactDecimal(parseUnits('1900750')) => '1.9M'
 * ```
 */
export const asCompactDecimal = flow(normalizeValue, formatAsCompactDecimal);

export const formatAsFixedDecimal = (decimals: number) => {
  const fixedDecimalFormatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return flow(fixedDecimalFormatter.format, truncateNegativeZero);
};

export const asDynamicFixedDecimal = (decimals: number) =>
  flow(normalizeValue, formatAsFixedDecimal(decimals));

export const formatAsUserBalance = (value: number): string => {
  const formatter =
    value >= 1
      ? new Intl.NumberFormat("en-US", {
          style: "decimal",
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      : new Intl.NumberFormat("en-US", {
          style: "decimal",
          maximumSignificantDigits: 3,
        });

  return formatter.format(value);
};

export const asUserBalance = flow(normalizeValue, formatAsUserBalance);

const fullUSDFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const singleCentsFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumSignificantDigits: 2,
});

export const formatAsFullUSD = (value: number): string => {
  if (value < 0.1) return singleCentsFormatter.format(value);

  return fullUSDFormatter.format(value);
};

/**
 * Formats a `number`, `string`, or `BigNumber` as a full US dollar amount, including cents.
 *
 * ```
 * asFullUSD(1000) => '$1,000.00'
 * asFullUSD('.5') => '$0.50'
 * asFullUSD(parseUnits('250500.345')) => '$250,500.35'
 * ```
 */
export const asFullUSD = flow(normalizeValue, formatAsFullUSD);

// TODO: apply this generically to all formatting utilities if required down the road

/**
 * Adjusts a value as if it had been formatted as a full USD value
 *
 * ```ts
 * // 0.9999 is formatted as $1.00, so 1 is returned
 * adjustToFullUSD(0.9999) // => 1
 * ```
 */
export const adjustToFullUSD = <T extends ValueToFormat>(value: T): T => {
  const originalValue = value;
  const adjustedValue = pipe(
    originalValue,
    asFullUSD,
    stripNonNumeric,
    parseBigIntWithDecimals(PRECISION_DECIMALS),
    ({ value, decimals }) =>
      match<ValueToFormat, ValueToFormat>(originalValue)
        .with(P.number, (): number =>
          pipe(formatUnits(value, decimals), parseNumber)
        )
        .with(P.string, (): string => formatUnits(value, decimals))
        .with(
          P.when(BigNumber.isBigNumber),
          (): BigNumber =>
            pipe(shiftDecimal(value, DEFAULT_DECIMALS - decimals), (bi) =>
              BigNumber.from(bi)
            )
        )
        .with(
          { bigNumber: P._ },
          ({ decimals: originalDecimals }): BigNumberWithDecimals => ({
            bigNumber: pipe(
              shiftDecimal(value, originalDecimals - decimals),
              (bi) => BigNumber.from(bi)
            ),
            decimals: originalDecimals,
          })
        )
        .with(P.bigint, (): bigint =>
          shiftDecimal(value, DEFAULT_DECIMALS - decimals)
        )
        .with(
          { value: P._ },
          ({ decimals: originalDecimals }): BigIntWithDecimals => ({
            value: shiftDecimal(value, originalDecimals - decimals),
            decimals: originalDecimals,
          })
        )
        .exhaustive()
  );

  return adjustedValue as T;
};

const wholeUSDFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const formatAsWholeUSD = wholeUSDFormatter.format;

export const asWholeUSD = flow(normalizeValue, formatAsWholeUSD);

const compactUSDFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
  notation: "compact",
});

export const formatAsCompactUSD = (value: number): string => {
  if (value < 999.995) return fullUSDFormatter.format(value);

  return compactUSDFormatter.format(value);
};

/**
 * Formats a `number`, `string`, or `BigNumber` as a compact US dollar amount.
 *
 * ```
 * asCompactUSD(500) => '$500.00'
 * asCompactUSD(1500) => '$1.5K'
 * asCompactUSD('900750') => '$900.8K'
 * asCompactUSD(parseUnits('1900750')) => '$1.9M'
 * ```
 */
export const asCompactUSD = flow(normalizeValue, formatAsCompactUSD);

export const pointsToPct = (value: number) => value / 100;

const precisePctFormat = {
  wholeNumber: new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }),
  maxDigits: new Intl.NumberFormat("en-US", {
    style: "decimal",
    maximumSignificantDigits: 3,
  }),
  maxDecimals: new Intl.NumberFormat("en-US", {
    style: "percent",
    maximumFractionDigits: 4,
  }),
};

/**
 * In the future, the `Intl.NumberFormat` `signDisplay` option has a `negative`
 * option that can be used to implement this behavior. But it does not currently
 * have much browser support:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat.
 */
const truncateNegativeZeroPct = (pct: string): string =>
  pct === "-0%" ? "0%" : pct;
const truncateNegativeZero = (pct: string): string =>
  parseFloat(pct) === 0 ? pct.replace(/-/g, "") : pct;

export const formatAsPrecisePct = (value: number): string => {
  if (Math.abs(value) >= 1) {
    return precisePctFormat.wholeNumber.format(value);
  }

  /**
   * Two formatters are used since `maximumSignificantDigits` and
   * `maximumFractionDigits` cannot currently be used together. The new
   * `roundingPriority` prop will ultimately fix this:
   * https://caniuse.com/mdn-javascript_builtins_intl_numberformat_numberformat_options_roundingpriority_parameter
   */
  return pipe(
    value,
    precisePctFormat.maxDigits.format,
    parseNumber,
    precisePctFormat.maxDecimals.format,
    truncateNegativeZeroPct
  );
};

/**
 * Formats a `number`, `string`, or `BigNumber` as a small percentage. Use this for percentages that are typically closer to 0%, like APY (~ 0-10% range).
 *
 * Uses 3 max significant digits and 4 max decimals.
 *
 * ```
 * asPrecisePct(0.0000123456789) => '0.0012%'
 * asPrecisePct(0.000123456789) => '0.0123%'
 * asPrecisePct(0.00123456789) => '0.123%'
 * asPrecisePct('0.1') => '10%'
 * asPrecisePct(parseUnits('0.123456789')) => '12.3%'
 * ```
 */
export const asPrecisePct = flow(normalizeValue, formatAsPrecisePct);

export const formatAsFixedPct = (decimals: number) => {
  const fixedPctFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return flow(fixedPctFormatter.format, truncateNegativeZero);
};

export const asDynamicFixedPct = (decimals: number) =>
  flow(normalizeValue, formatAsFixedPct(decimals));

/**
 * Formats a `number`, `string`, or `BigNumber` as a percentage with 2 decimals. Use this for percentages that need to be compared.
 *
 * ```
 * asFixedPct(0.00123456789) => '0.12%'
 * asFixedPct('0.1') => '10.00%'
 * asFixedPct(parseUnits('0.123456789')) => '12.35%'
 * ```
 */
export const asFixedPct = asDynamicFixedPct(2);

export const asApr = asDynamicFixedPct(1);

/**
 * Formats points as a `number`, `string`, or `BigNumber` to a small percentage. Use this for percentages that are typically closer to 0%, like APY (~ 0-10% range).
 *
 * Uses 3 max significant digits and 4 max decimals.
 *
 * ```
 * pointsToPrecisePct(0.123456789) => '0.123%'
 * pointsToPrecisePct('10') => '10%'
 * pointsToPrecisePct(parseUnits('12.3456789')) => '12.3%'
 * ```
 */
export const pointsToPrecisePct = flow(
  normalizeValue,
  pointsToPct,
  formatAsPrecisePct
);

const wholePctFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const formatAsWholePct = flow(
  wholePctFormatter.format,
  truncateNegativeZeroPct
);

/**
 * Formats a `number`, `string`, or `BigNumber` as a large percentage. Use this for percentages that could range from 0% to 100%, like health factor.
 *
 * ```
 * asWholePct(0.00123456789) => '0%'
 * asWholePct(0.1) => '10%'
 * asWholePct(0.56789) => '57%'
 * asWholePct('1') => '100%'
 * asWholePct(parseUnits('123456.789')) => '12,345,679%'
 * ```
 */
export const asWholePct = flow(normalizeValue, formatAsWholePct);

/**
 * Formats points as a `number`, `string`, or `BigNumber` to a large percentage. Use this for percentages that could range from 0% to 100%, like health factor.
 *
 * ```
 * pointsToWholePct(0.123456789) => '0%'
 * pointsToWholePct(10) => '10%'
 * pointsToWholePct(56.789) => '57%'
 * pointsToWholePct('100') => '100%'
 * pointsToWholePct(parseUnits('12345678.9')) => '12,345,679%'
 * ```
 */
export const pointsToWholePct = flow(
  normalizeValue,
  pointsToPct,
  formatAsWholePct
);

export const formatAsPreciseWithDecimalsPct = (value: number): string => {
  const absValue = Math.abs(value);

  if (Math.abs(value) >= 10) {
    return precisePctFormat.wholeNumber.format(value);
  }

  const lessThan1Pct = absValue < 0.01;
  const lessThan10Pct = absValue < 0.1;
  const formatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    maximumSignificantDigits: lessThan1Pct ? 2 : lessThan10Pct ? 3 : 4,
  });

  return formatter.format(value);
};

/**
 * Formats value to a small percentage. Use this for percentages that are typically closer to 0%, like APY (~ 0-10% range).
 *
 * Uses 2 max significant digits, with no limit on decimals, for percentages less than 1%.
 * Uses 3 max significant digits, with no limit on decimals, for percentages less than 10%.
 * Uses 4 max significant digits for values above 10%.
 * Uses whole number for values above 1,000%.
 */
export const asPrecisePctWithDecimals = flow(
  normalizeValue,
  formatAsPreciseWithDecimalsPct
);

/**
 * Formats points as a `number`, `string`, or `BigNumber` to a small percentage. Use this for percentages that are typically closer to 0%, like APY (~ 0-10% range).
 *
 * Uses 2 max significant digits, with no limit on decimals, for percentages less than 1%.
 * Uses 3 max significant digits, with no limit on decimals, for percentages less than 10%.
 * Uses 4 max significant digits for values above 10%.
 * Uses whole number for values above 1,000%.
 */
export const pointsToPrecisePctWithDecimals = flow(
  normalizeValue,
  pointsToPct,
  formatAsPreciseWithDecimalsPct
);

/**
 * Converts a formatted percentage to formatted percentage points.
 *
 * ```
 * formattedPctToFormattedPoints('12.3%') => '12.3 PP'
 * ```
 */
export const formattedPctToFormattedPoints = (pct: string) =>
  pct.replace(/%/g, " PP");

/**
 * Formats a number to a decimal value. Use this for values that are typically closer to 0.
 *
 * Uses 2 max significant digits, with no limit on decimals, for values less than 1.
 * Uses 3 max significant digits, with no limit on decimals, for values less than 10.
 * Uses 4 max significant digits for values above 10.
 */
export const formatAsPreciseDecimalsWithDecimals = (value: number): string => {
  const lessThan = value < 1;
  const lessThan10 = value < 10;
  const formatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
    maximumSignificantDigits: lessThan ? 2 : lessThan10 ? 3 : 4,
  });

  return formatter.format(value);
};
